import React, { useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { calcLineByModel, formatCurrency, formatDate } from '../booking.utils';
import type { DetailLineUI } from './RoomRateSection';
import { priceCommercialLine } from './CommercialLinesSection';
import type { CommercialLineRow } from './CommercialLinesSection';

// ============================================================================
// TYPES
// ============================================================================

interface BookingSummaryPanelProps {
  detailLines: DetailLineUI[];
  /**
   * Per-pax priced lines. When present THEY are the money — the detail lines
   * are allocation only and would price to zero, so rendering from them would
   * show a ₹0 summary against a real server total: the irreconcilable-figures
   * bug, again. Priced with the same exported function the modal's subtotal
   * uses, so the two cannot drift.
   */
  commercialLines?: CommercialLineRow[];
  numNights: number;
  checkInDate: string;
  checkOutDate: string;
  guestName: string;
  childAges?: (number | undefined)[];
  loading?: boolean;
  /** Property-level pricing model. Defaults to 'a_la_carte' on unrecognized input. */
  pricingModel?: string;
  /** Booking flagged complimentary — discount = subtotal, net = 0. */
  isComplimentary?: boolean;
  /** Computed discount in rupees (server formula mirrored on client). */
  discountAmount?: number;
  /** Sum of component rate PREMIUMS (sold above rack). */
  adjustmentPremium?: number;
  /** Sum of component rate DISCOUNTS as a positive number. */
  adjustmentDiscount?: number;
  /** Final payable. Equals subtotal when no discount, 0 for complimentary. */
  netAmount?: number;
  /** What the user picked — drives the "Discount (X% / Flat ₹Y)" label. */
  discountType?: 'Percentage' | 'FlatAmount' | '';
  discountValue?: number;
  /** Approval pill. Tooltip carries the trigger text. */
  requiresApproval?: boolean;
  approvalReasons?: string;
  /**
   * Ancillary add-on lines (spa, flowers, etc.) attached to this booking.
   * Rendered BELOW the Net Total — these are charged in addition and are NOT
   * affected by discount. Empty array hides the block.
   */
  addonLines?: Array<{
    label: string;
    quantity: number;
    unitAmount: number;
    lineAmount: number;
  }>;
}

// ============================================================================
// COMPONENT
// ============================================================================

const BookingSummaryPanel: React.FC<BookingSummaryPanelProps> = ({
  detailLines, commercialLines, numNights, checkInDate, checkOutDate, guestName, childAges, loading, pricingModel,
  isComplimentary, discountAmount, netAmount, discountType, discountValue,
  adjustmentPremium, adjustmentDiscount,
  requiresApproval, approvalReasons, addonLines,
}) => {
  // Add-on aggregate (sum of all line amounts). Computed locally so the summary
  // panel stays in sync even when the parent hasn't refreshed yet.
  const addonsTotal = (addonLines ?? []).reduce((sum, l) => sum + l.lineAmount, 0);
  const hasAddons = (addonLines ?? []).length > 0;
  const effectiveNet = netAmount ?? 0;
  const finalGrandTotal = effectiveNet + addonsTotal;
  const isPerPax = (commercialLines?.length ?? 0) > 0;
  const perPaxTotals = useMemo(() => {
    const src = commercialLines ?? [];
    let room = 0; let meal = 0;
    const lines = src.map((l) => {
      const p = priceCommercialLine(l, Math.max(1, numNights));
      room += p.room; meal += p.meal;
      return { ...l, ...p };
    });
    return { lines, roomCharges: room, mealCharges: meal, subtotal: room + meal };
  }, [commercialLines, numNights]);

  const totals = useMemo(() => {
    let roomCharges = 0;
    let extraCharges = 0;
    let mealCharges = 0;
    const lines = detailLines.map(line => {
      // Prefer the line's own snapshot model (captured at booking time) so the
      // totals on view/edit always match the stored numbers. Fall back to the
      // property's current config for freshly-added lines in create mode that
      // haven't been snapshotted yet.
      const effectiveModel = line.pricingModel || pricingModel;
      const charges = calcLineByModel(effectiveModel, line, numNights, childAges);
      roomCharges += charges.roomCharges;
      extraCharges += charges.extraCharges;
      mealCharges += charges.mealCharges;
      return { ...line, lineTotal: charges.lineTotal };
    });
    const subtotal = roomCharges + extraCharges + mealCharges;
    return { lines, roomCharges, extraCharges, mealCharges, subtotal };
  }, [detailLines, numNights, childAges, pricingModel]);

  // Derive tax indicator from selected rate plans
  const taxIndicator = useMemo(() => {
    if (detailLines.length === 0) return null;
    const allInclusive = detailLines.every(l => l.includeTaxes === true);
    return allInclusive ? 'Tax Inclusive' : 'Tax Exclusive';
  }, [detailLines]);

  const hasLines = isPerPax || detailLines.length > 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      {/* Header info */}
      <div style={{ marginBottom: '0.75rem' }}>
        <div style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '0.25rem' }}>
          Booking Summary
        </div>
        {guestName && (
          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
            <IconSystem.user size={12} style={{ marginRight: '0.25rem' }} />
            {guestName}
          </div>
        )}
        {checkInDate && checkOutDate && (
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.125rem' }}>
            {formatDate(checkInDate)} → {formatDate(checkOutDate)}
            {numNights > 0 && ` (${numNights} night${numNights !== 1 ? 's' : ''})`}
          </div>
        )}
      </div>

      <hr className="booking-summary-divider" />

      {/* Room lines */}
      {!hasLines ? (
        <div style={{ padding: '1.5rem 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
          {loading ? 'Loading booking data...' : 'Add rooms to see pricing'}
        </div>
      ) : (
        <div className="booking-summary-section">
          {isPerPax
            ? perPaxTotals.lines.map((line, idx) => (
              <div key={idx} style={{ marginBottom: '0.5rem' }}>
                <div className="booking-summary-row" style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                  <span>{line.description || 'Line'} · {line.pax} pax</span>
                  <span>{formatCurrency(line.amount)}</span>
                </div>
                <div className="booking-summary-row line-item">
                  <span>
                    {numNights} night{numNights !== 1 ? 's' : ''} @ {formatCurrency(line.ratePerPax || 0)}/pax
                    {line.mealPlanCode ? ` · ${line.mealPlanCode}` : ''}
                  </span>
                </div>
              </div>
            ))
            : totals.lines.map((line, idx) => (
              <div key={idx} style={{ marginBottom: '0.5rem' }}>
                <div className="booking-summary-row" style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                  <span>{line.categoryName || 'Room'} × {line.numberOfRooms}</span>
                  <span>{formatCurrency(line.lineTotal)}</span>
                </div>
                <div className="booking-summary-row line-item">
                  <span>{numNights} night{numNights !== 1 ? 's' : ''} @ {formatCurrency(line.ratePerRoom)}</span>
                </div>
              </div>
            ))}
        </div>
      )}

      {hasLines && (
        <>
          <hr className="booking-summary-divider" />

          <div className="booking-summary-section">
            <div className="booking-summary-row">
              <span>Room Charges</span>
              <span>{formatCurrency(isPerPax ? perPaxTotals.roomCharges : totals.roomCharges)}</span>
            </div>
            {!isPerPax && totals.extraCharges > 0 && (
              <div className="booking-summary-row">
                <span>Extra Charges</span>
                <span>{formatCurrency(totals.extraCharges)}</span>
              </div>
            )}
            {(isPerPax ? perPaxTotals.mealCharges : totals.mealCharges) > 0 && (
              <div className="booking-summary-row">
                <span>Meal Charges</span>
                <span>{formatCurrency(isPerPax ? perPaxTotals.mealCharges : totals.mealCharges)}</span>
              </div>
            )}
          </div>

          {/* Subtotal — always shown so management can see the original cost on comp bookings.
              With rate adjustments this is the STANDARD (rack) subtotal; the
              selling subtotal follows below. */}
          <div className="booking-summary-total" style={{ fontSize: '0.85rem' }}>
            <span>{(adjustmentPremium ?? 0) > 0 || (adjustmentDiscount ?? 0) > 0 ? 'Standard subtotal' : 'Subtotal'}</span>
            <span>{formatCurrency(isPerPax ? perPaxTotals.subtotal : totals.subtotal)}</span>
          </div>

          {/* Component-wise rate adjustments — premiums and discounts shown
              separately so the negotiation is never ambiguous internally. */}
          {(adjustmentPremium ?? 0) > 0 && (
            <div className="booking-summary-row" style={{ color: '#b45309', marginTop: '0.25rem' }}>
              <span>Rate premiums</span>
              <span>+{formatCurrency(adjustmentPremium ?? 0)}</span>
            </div>
          )}
          {(adjustmentDiscount ?? 0) > 0 && (
            <div className="booking-summary-row" style={{ color: '#b91c1c' }}>
              <span>Rate discounts</span>
              <span>-{formatCurrency(adjustmentDiscount ?? 0)}</span>
            </div>
          )}
          {((adjustmentPremium ?? 0) > 0 || (adjustmentDiscount ?? 0) > 0) && (
            <div className="booking-summary-total" style={{ fontSize: '0.85rem', marginTop: '0.25rem' }}>
              <span>Selling subtotal</span>
              <span>{formatCurrency(totals.subtotal + (adjustmentPremium ?? 0) - (adjustmentDiscount ?? 0))}</span>
            </div>
          )}

          {/* Discount line — visible whenever a discount is applied OR booking is comp. */}
          {(isComplimentary || (discountAmount ?? 0) > 0) && (
            <div className="booking-summary-row" style={{ color: '#b45309', marginTop: '0.25rem' }}>
              <span>
                {isComplimentary
                  ? 'Complimentary booking'
                  : (discountType === 'Percentage'
                      ? `Discount (${discountValue ?? 0}%)`
                      : discountType === 'FlatAmount'
                        ? `Discount (Flat ₹${(discountValue ?? 0).toLocaleString('en-IN')})`
                        : 'Discount')}
              </span>
              <span>-{formatCurrency(discountAmount ?? 0)}</span>
            </div>
          )}

          {/* Net Total — replaces "Total" once a discount/comp is applied. */}
          <div className="booking-summary-total" style={{ marginTop: '0.25rem' }}>
            <span>{(isComplimentary || (discountAmount ?? 0) > 0) ? 'Net Total' : 'Total'}</span>
            <span>{formatCurrency(netAmount ?? totals.subtotal)}</span>
          </div>

          {isComplimentary && (
            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.125rem' }}>
              Original cost retained for service & cost reporting.
            </div>
          )}

          {/* Ancillary add-ons — charged IN ADDITION to the Net Total, NOT
              affected by discount. Block hides entirely when no add-ons added. */}
          {hasAddons && (
            <>
              <hr className="booking-summary-divider" />
              <div className="booking-summary-section">
                <div className="booking-summary-row" style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                  <span>Add-ons</span>
                  <span>{formatCurrency(addonsTotal)}</span>
                </div>
                {(addonLines ?? []).map((line, idx) => (
                  <div key={idx} className="booking-summary-row line-item">
                    <span>{line.label} × {line.quantity} @ {formatCurrency(line.unitAmount)}</span>
                    <span>{formatCurrency(line.lineAmount)}</span>
                  </div>
                ))}
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.125rem' }}>
                  Discount does not apply to add-ons.
                </div>
              </div>

              <div className="booking-summary-total" style={{ marginTop: '0.25rem' }}>
                <span>Grand Total (with Add-ons)</span>
                <span>{formatCurrency(finalGrandTotal)}</span>
              </div>
            </>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.375rem', marginTop: '0.375rem', flexWrap: 'wrap' }}>
            {requiresApproval && (
              <span
                style={{
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  padding: '0.125rem 0.5rem',
                  borderRadius: '0.25rem',
                  background: '#fee2e2',
                  color: '#991b1b',
                }}
                title={approvalReasons || 'Booking will require approval before confirmation.'}
              >
                Approval Required
              </span>
            )}
            {taxIndicator && (
              <span style={{
                fontSize: '0.7rem',
                fontWeight: 600,
                padding: '0.125rem 0.5rem',
                borderRadius: '0.25rem',
                background: taxIndicator === 'Tax Inclusive' ? 'var(--badge-active-bg, #dcfce7)' : 'var(--badge-warning-bg, #fef3c7)',
                color: taxIndicator === 'Tax Inclusive' ? 'var(--badge-active-text, #166534)' : 'var(--badge-warning-text, #92400e)',
              }}>{taxIndicator}</span>
            )}
          </div>
        </>
      )}

    </div>
  );
};

export default BookingSummaryPanel;
